import { decodeHtmlEntities } from '../utils/html'

/**
 * Collection decoupled → vocabulaire d'une facette Elasticsearch.
 *
 * GÉNÉRIQUE et piloté par l'URL : le processor ne connaît ni entité, ni domaine.
 * Ce qu'il faut lire et comment fabriquer la valeur est déclaré dans le YAML
 * (donc du CONTENU), sur le modèle du marqueur `rawSyncable=1` déjà en place
 * dans [decoupledApiProcessors.js](./decoupledApiProcessors.js) :
 *
 *   /decoupled-api/{entity}/{bundle}/collection?display=decoupled&asFacet=1
 *     &facetValue=id        # champ portant la valeur   (défaut: id)
 *     &facetLabel=name      # champ portant le libellé  (défaut: name)
 *     &facetPrefix=group:   # préfixe de la valeur       (défaut: aucun)
 *
 * Rend les quatre formes dont une facette a besoin, plus `ok` :
 *
 *   { values: ["group:5"],                       // pré-cocher (FacetList.dataLocation)
 *     labels: { "group:5": "EA Lab" },           // étiqueter les buckets (labelMap)
 *     options: [{ value, label }],               // liste de choix (FuzzySelectField…)
 *     ok: true,                                  // false : erreur ou réponse illisible
 *     buckets: [{ key: "group:5" }] }            // vocabulaire d'une FacetList
 *
 * `buckets` est la MÊME liste que `options`, à la forme d'une agrégation
 * Elasticsearch (`[{key}]`) : c'est ce que `FacetList` sait lire. Sans elle, une
 * facette dont le vocabulaire est une collection decoupled (et non un index ES)
 * n'aurait aucun moyen déclaratif d'être alimentée. Pas de `doc_count` : rien ici
 * ne compte de documents — la facette se rend avec `showCount: false`.
 *
 * OPT-IN STRICT : sans `asFacet=1` la réponse ressort INTACTE. Contrairement aux
 * processors qui s'accrochent à un chemin d'URL (et finissent avec une liste
 * d'exceptions, cf. `taxonomyToOptionsProcessor`), celui-ci ne détourne jamais
 * la réponse d'un écran qui ne l'a pas demandé.
 *
 * POURQUOI DU JS (justification config-first) : une facette filtre sur la valeur
 * INDEXÉE, qui n'est presque jamais l'identifiant brut de l'entité — sur
 * /crm/deals le champ `es_group_grants` porte `group:5` quand decoupled_toolbox
 * rend `{id: 5}`. Or reactive-json ne sait pas transformer une donnée :
 * `simpleMapping` DÉPLACE une valeur, `collectField` est une action qui exige un
 * événement, et l'interpolation ne concatène que dans une URL. Sans ce
 * ré-étiquetage une facette ne peut donc être ni pré-cochée ni lisible. C'est une
 * NORMALISATION de réponse — la raison d'être du type `dataProcessor` — et elle
 * est ici écrite UNE fois pour toutes les facettes, pas par cas d'usage.
 *
 * Enregistré dans [App.jsx](../App.jsx) sous `dataProcessor['collection-to-facet']`.
 */
export const COLLECTION_TO_FACET_PARAM = 'asFacet=1'

export const collectionToFacetProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  const url = String(requestContext?.url ?? '')
  if (!url.includes(COLLECTION_TO_FACET_PARAM)) return dataToProcess

  // `ok` distingue « collection lue, peut-être vide » de « lecture ratée » : un
  // écran qui exige un choix dans `options` s'y fie pour ne pas prendre une
  // erreur pour une liste vide.
  const empty = { values: [], labels: {}, options: [], buckets: [], ok: false }
  // Sur erreur on rend la forme vide : la page reste utilisable, simplement sans
  // pré-sélection ni libellés (jamais de facette pré-cochée « au hasard »).
  if (responseContext?.status >= 400) return empty
  if (!Array.isArray(dataToProcess)) return empty

  // Les URLs sont relatives : une base bidon suffit à réutiliser le parseur natif.
  const params = new URL(url, 'http://localhost').searchParams
  const valueKey = params.get('facetValue') || 'id'
  const labelKey = params.get('facetLabel') || 'name'
  const prefix = params.get('facetPrefix') || ''

  const values = []
  const labels = {}
  const options = []
  const buckets = []

  for (const item of dataToProcess) {
    // Collection de Syncables ({item_url, data}) ou objets à plat selon le display.
    const record = item?.data ?? item
    const raw = record?.[valueKey]
    if (raw === undefined || raw === null || raw === '') continue
    const value = `${prefix}${raw}`
    if (Object.prototype.hasOwnProperty.call(labels, value)) continue
    const label = decodeHtmlEntities(String(record[labelKey] ?? '')) || value
    values.push(value)
    labels[value] = label
    options.push({ value, label })
    buckets.push({ key: value })
  }

  return { values, labels, options, buckets, ok: true }
}
